import { ReactNode, useEffect, useRef, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { stepUp } from '../api/purge';
import { ApiError } from '../api/client';
import type { Env } from '../types';

/**
 * Modale de confirmation d'une action destructrice, en trois temps :
 *   1. reconnexion Google (le compte doit etre celui de la session) ;
 *   2. saisie du nombre exact d'elements concernes ;
 *   3. le bouton d'action.
 * `onConfirm` fait l'appel ; s'il repond STEP_UP_REQUIRED (session trop
 * ancienne), la modale revient a l'etape 1 au lieu d'echouer.
 */
export function StepUpModal({
  env,
  title,
  count,
  confirmLabel,
  danger = true,
  children,
  onConfirm,
  onClose,
}: {
  readonly env: Env;
  readonly title: string;
  readonly count: number;
  readonly confirmLabel: string;
  readonly danger?: boolean;
  readonly children?: ReactNode;
  readonly onConfirm: () => Promise<void>;
  readonly onClose: () => void;
}) {
  const [authed, setAuthed] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gsiRef = useRef<HTMLDivElement>(null);
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Bouton Google Identity Services : un clic = un jeton frais, echange contre
  // une session marquee stepUpAt. Le meme compte que la session est impose cote API.
  useEffect(() => {
    if (authed) return;
    const g = window.google;
    const el = gsiRef.current;
    if (!g || !el || !clientId) return;
    g.accounts.id.initialize({
      client_id: clientId,
      hd: 'zce-group.com',
      auto_select: false,
      callback: async (resp: { credential: string }) => {
        setError(null);
        try {
          await stepUp(resp.credential);
          setAuthed(true);
        } catch (e) {
          setError((e as Error).message);
        }
      },
    });
    el.innerHTML = '';
    g.accounts.id.renderButton(el, {
      theme: 'filled_blue',
      size: 'medium',
      text: 'continue_with',
      shape: 'pill',
      width: 260,
    });
  }, [authed, clientId]);

  const countOk = Number(typed) === count;

  async function confirm() {
    if (!authed || !countOk || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'STEP_UP_REQUIRED') {
        setAuthed(false);
        setError('La reconnexion a expire, recommencez.');
      } else {
        setError((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="panel w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="panel-header">
          <h2 className="panel-title flex items-center gap-2">
            <ShieldAlert className={`h-4 w-4 ${danger ? 'text-red-400' : 'text-cyan-400'}`} />
            {title}
          </h2>
          <span className={env === 'prod' ? 'pill-env-prod' : 'pill-env-staging'}>
            <span className="pill-dot" />
            {env}
          </span>
        </div>

        <div className="p-5 space-y-5">
          {children}

          {/* Etape 1 : reconnexion */}
          <div className="space-y-2">
            <div className="label">1. Confirmez votre identite</div>
            {authed ? (
              <div className="text-[13px] rounded-md border note-green px-3 py-2">
                Identite confirmee.
              </div>
            ) : (
              <>
                <p className="text-[12px] t-muted">
                  Reconnectez-vous avec le <strong className="t-strong">meme compte Google</strong>{' '}
                  que votre session.
                </p>
                <div ref={gsiRef} />
                {!clientId && (
                  <p className="text-red-400 text-sm">VITE_GOOGLE_CLIENT_ID non configure.</p>
                )}
              </>
            )}
          </div>

          {/* Etape 2 : le nombre */}
          <div className="space-y-2">
            <label htmlFor="stepup-count" className="label">
              2. Saisissez le nombre d&apos;elements : <span className="t-strong num">{count}</span>
            </label>
            <input
              id="stepup-count"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.target.value.trim())}
              placeholder={String(count)}
              className="input w-40"
              disabled={!authed || busy}
            />
          </div>

          {error && <div className="pill pill-red !whitespace-normal py-1.5">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t bd">
          <button type="button" onClick={onClose} className="btn-outline" disabled={busy}>
            Annuler
          </button>
          <button
            type="button"
            onClick={confirm}
            className={danger ? 'btn-danger' : 'btn'}
            disabled={!authed || !countOk || busy}
          >
            {busy ? 'En cours...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
